/**
 * API Configuration
 * Centralized API setup and utilities
 */

import axios from 'axios';
import { applyCurrentFrontendRouteHeader, getFrontendRouteHeader } from '../../utils.js';
import {
  captureAxiosError,
  captureAxiosResponse,
  markAxiosRequestStarted,
} from '../../utils/posthog';

// API Base URL
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://vwwjda2e99.us-west-2.awsapprunner.com ';

const shouldLogTokenChecks = process.env.REACT_APP_DEBUG_TOKEN_CHECKS === 'true';

// Create axios instance with defaults
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 2 minutes for large file operations
  headers: {
    'Content-Type': 'application/json',
  },
});

// ─── Token helpers ────────────────────────────────────────────────────────────

// Decode JWT payload without any verification — just read the claims
const decodeJWTPayload = (token) => {
  try {
    const base64Payload = token.split('.')[1];
    return JSON.parse(atob(base64Payload));
  } catch {
    return null;
  }
};

// Returns true if token is missing, unparseable, or expires within 60 seconds
const isTokenExpired = (token) => {
  if (!token) return true;
  const payload = decodeJWTPayload(token);
  if (!payload || !payload.exp) return true;

  const nowSec = Date.now() / 1000;
  const secondsLeft = Math.round(payload.exp - nowSec);
  const expiresAt = new Date(payload.exp * 1000).toLocaleTimeString();
  const expired = nowSec >= (payload.exp - 60);

  if (shouldLogTokenChecks) {
    console.log(`🕐 [TokenCheck] exp=${expiresAt} | secondsLeft=${secondsLeft}s | ${expired ? '❌ EXPIRED (will refresh)' : '✅ valid'}`);
  }

  return expired;
};

// Skip token check for auth endpoints — signin/refresh/etc. don't need token refresh
const isRefreshCall = (url) => url && url.includes('/auth/');

// ─── Refresh state — shared by all interceptors ───────────────────────────────

let isRefreshing = false;
let refreshQueue = [];

// ─── 403 dedup — one toast per burst of concurrent denied requests ────────────
let accessDeniedToastActive = false;

const FEATURE_LABELS = {
  patientManagement: 'Patient Management',
  labsVendors: 'Labs & Vendors',
  procedures: 'Procedures',
  calendars: 'Calendars',
  dispensing: 'Dispensing',
  supplements: 'Supplements',
  rooms: 'Rooms',
  assignees: 'Assignees',
  treatmentTemplates: 'Treatment Templates',
  templates: 'Templates',
  emailTemplates: 'Email Templates',
  additionalResources: 'Additional Resources',
};

// Ordered: more-specific prefixes first.
const URL_PAGE_MAP = [
  { prefix: '/labtracker',                  label: 'Lab Tracker' },
  { prefix: '/labtrackerrange',             label: 'Lab Tracker' },
  { prefix: '/search/labtrackers',          label: 'Lab Tracker' },
  { prefix: '/globalsearch/labtrackers',    label: 'Lab Tracker' },
  { prefix: '/labresults',                  label: 'Lab Tracker' },
  { prefix: '/update-g6pd-range',           label: 'Lab Tracker' },
  { prefix: '/rules-to-follow',             label: 'Rules to Follow' },
  { prefix: '/api/soap-note-templates',     label: 'SOAP Notes' },
  { prefix: '/api/soap-note-sub-and-obj-templates', label: 'SOAP Notes' },
  { prefix: '/api/soap=note-templates',     label: 'SOAP Notes' },
  { prefix: '/api/soap-note-templates-with-type', label: 'SOAP Notes' },
  { prefix: '/soap-note',                   label: 'SOAP Notes' },
  { prefix: '/get-soap-note',               label: 'SOAP Notes' },
  { prefix: '/get-soap-notes',              label: 'SOAP Notes' },
  { prefix: '/soap-notes-with-treatment',   label: 'SOAP Notes' },
  { prefix: '/check-soapnote-exists',       label: 'SOAP Notes' },
  { prefix: '/check-order-soapnote',        label: 'SOAP Notes' },
  { prefix: '/soap-item-delete',            label: 'SOAP Notes' },
  { prefix: '/get-all-encounters',          label: 'EMR' },
  { prefix: '/delete-encounters',           label: 'EMR' },
  { prefix: '/active-dispensed-items',      label: 'EMR' },
  { prefix: '/active-supplements-items',    label: 'EMR' },
  { prefix: '/emr',                         label: 'EMR' },
  { prefix: '/update-emr-custom-forms',     label: 'EMR' },
  { prefix: '/get-emr-custom-forms',        label: 'EMR' },
  { prefix: '/add-emr-custom-form',         label: 'EMR' },
  { prefix: '/update-emr-custom-form',      label: 'EMR' },
  { prefix: '/delete-emr-custom-form',      label: 'EMR' },
  { prefix: '/update-emr-supplement',       label: 'EMR' },
  { prefix: '/get-emr-supplements',         label: 'EMR' },
  { prefix: '/update-emr-dispensed',        label: 'EMR' },
  { prefix: '/get-emr-dispensed',           label: 'EMR' },
  { prefix: '/patient/schedules',           label: 'Schedule' },
  { prefix: '/patient-schedules',           label: 'Schedule' },
  { prefix: '/documentsApproval',           label: 'Documents' },
  { prefix: '/documents-approval',          label: 'Documents' },
  { prefix: '/file-exchange-in-document',   label: 'Documents' },
  { prefix: '/delete-public-file',          label: 'Documents' },
  { prefix: '/patients-with-pending-approvals', label: 'Documents' },
  { prefix: '/process-all-documents',       label: 'Documents' },
  { prefix: '/fix-fax-download-urls',       label: 'Documents' },
  { prefix: '/update-patient-folder-names', label: 'Documents' },
  { prefix: '/disconnect-documents-authorization', label: 'Documents' },
  { prefix: '/user-groups',                 label: 'Admin Panel' },
  { prefix: '/patient-admin',               label: 'Patient Management' },
  { prefix: '/patient-profiles',            label: 'Patient Management' },
  { prefix: '/site-settings',               label: 'Admin Panel' },
  { prefix: '/add-allowed-users',           label: 'Admin Panel' },
  { prefix: '/get-all-emails',              label: 'Admin Panel' },
  { prefix: '/add-email',                   label: 'Admin Panel' },
  { prefix: '/delete-email',                label: 'Admin Panel' },
  { prefix: '/disconnect-sender-email-authorization', label: 'Documents' },
  { prefix: '/api/setup-gmail-webhook',     label: 'Gmail Settings' },
  { prefix: '/api/stop-gmail-webhook',      label: 'Gmail Settings' },
  { prefix: '/api/patientform',             label: 'Patient Forms' },
  { prefix: '/api/reviews',                 label: 'Patient Reviews' },
  { prefix: '/consent-forms',               label: 'Consent Forms' },
  { prefix: '/api/ingredient-templates',    label: 'Supplements' },
  { prefix: '/ingredient',                  label: 'Supplements' },
  { prefix: '/import-supplements',          label: 'Admin Panel' },
  { prefix: '/shopify/patients/sync',       label: 'Admin Panel' },
  { prefix: '/shopify/customers',           label: 'Admin Panel' },
  { prefix: '/getshopifyorders',            label: 'Admin Panel' },
  { prefix: '/batch/shopify-orders',        label: 'Shopify Orders' },
  { prefix: '/restore-shopify-orders',      label: 'Admin Panel' },
  { prefix: '/compare-shopify-orders',      label: 'Admin Panel' },
  { prefix: '/send-sms',                    label: 'Messaging' },
  { prefix: '/get-conversation',            label: 'Messaging' },
  { prefix: '/queue-status',                label: 'Queue Status' },
  { prefix: '/debug',                       label: 'Debug Tools' },
];

function getPageLabelFromUrl(url) {
  if (!url) return null;
  const path = url.replace(/^https?:\/\/[^/]+/, '').replace(/\?.*$/, '');
  for (const { prefix, label } of URL_PAGE_MAP) {
    if (path === prefix || path.startsWith(prefix + '/')) return label;
  }
  return null;
}

function build403Message(data, url) {
  const { reason, required, feature } = data || {};
  const pageLabel = getPageLabelFromUrl(url);

  if (reason === 'missing_page') {
    if (pageLabel) return `Access Denied — you don't have access to ${pageLabel}.`;
    if (Array.isArray(required) && required.length === 1) {
      const name = { lab: 'Lab Tracker', emr: 'EMR', emrV2: 'EMR v2', frontDesk: 'Front Desk', admin: 'Admin', patientForms: 'Patient Forms', schedule: 'Schedule', rulesToFollow: 'Rules to Follow', treatmentView: 'Treatment View', auditView: 'Audit View', todayView: 'Today View' }[required[0]] || required[0];
      return `Access Denied — you don't have access to ${name}.`;
    }
    return 'Access Denied — you don\'t have access to this page.';
  }

  if (reason === 'missing_admin_feature' && feature) {
    const featureLabel = FEATURE_LABELS[feature] || feature;
    return pageLabel
      ? `Access Denied — ${pageLabel} requires ${featureLabel} admin permission.`
      : `Access Denied — requires ${featureLabel} admin permission.`;
  }

  if (reason === 'admin_only') {
    const context = data.label || pageLabel;
    return context
      ? `Access Denied — ${context} is restricted to administrators.`
      : 'Access Denied — this action is restricted to administrators.';
  }

  return 'Access Denied — you do not have permission to perform this action.';
}

// antd's message is loaded on first use, so antd stays out of the main bundle (every
// page that uses antd loads it anyway).
let antdMessagePromise = null;
const loadAntdMessage = () => {
  if (!antdMessagePromise) {
    antdMessagePromise = import('antd').then((antd) => antd.message).catch((error) => {
      antdMessagePromise = null;
      throw error;
    });
  }
  return antdMessagePromise;
};

export function showAccessDenied(data, url) {
  if (accessDeniedToastActive) return;
  accessDeniedToastActive = true;
  const content = typeof data === 'string' ? data : build403Message(data, url);
  loadAntdMessage()
    .then((message) => {
      message.error({
        content,
        duration: 4,
        onClose: () => { accessDeniedToastActive = false; },
      });
    })
    .catch(() => { accessDeniedToastActive = false; });
}

const processQueue = (error, token = null) => {
  refreshQueue.forEach(({ resolve, reject }) => error ? reject(error) : resolve(token));
  refreshQueue = [];
};

// Pages a patient opens from a link, with no login (RoutesWrapper's public routes; React
// Router matches them in any case). A staff login left over in this browser that has
// ended is cleared there, but the patient is not sent to the staff login page, and a
// request that can't refresh that login goes out without it (these pages need none).
const PUBLIC_PAGE_PATHS = [
  /^\/patientform\/(?!preview(\/|$))/i,
  /^\/publicdocumentsupload\//i,
  /^\/shared-documents\//i,
  /^\/reviewform\//i,
];
const onPublicPage = () => PUBLIC_PAGE_PATHS.some((path) => path.test(window.location?.pathname || ''));

// The request as a patient page sends it: no staff login, and none looked for.
const withoutLogin = (config) => {
  if (typeof config.headers?.delete === 'function') config.headers.delete('Authorization');
  else if (config.headers) delete config.headers.Authorization;
  config._skipTokenCheck = true;
  return config;
};

/** Sends a signed-out user to the login page, unless they are on a public patient page. */
export const goToLoginUnlessPublicPage = () => {
  if (!onPublicPage()) window.location.href = '/login';
};

const sessionEndedError = (message) => Object.assign(new Error(message), { sessionEnded: true });

// Everything a signed-in session keeps in this browser.
const SESSION_KEYS = [
  'sessionToken', 'cognitoAccessToken', 'cognitoIdToken', 'cognitoRefreshToken',
  'cognitoUser', 'user', 'userShowName', 'lastActivity',
];
const endSession = () => {
  SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
  goToLoginUnlessPublicPage();
};

// Signs out: for a refresh failure whose error has sessionEnded set. Any other failure
// (the sign-in service down, no connection) keeps the session: the request that needed
// the refresh fails on its own and a later one tries again.
const endSessionIfOver = (error) => {
  if (error?.sessionEnded) endSession();
};

const REFRESH_TIMEOUT_MS = 30000;

// Core refresh logic — called by both proactive (request) and reactive (response) paths.
// Resolves the token to use; a rejection has sessionEnded set when the session is over.
const doRefresh = async () => {
  const storedRefreshToken = localStorage.getItem('cognitoRefreshToken');

  if (!storedRefreshToken) {
    throw sessionEndedError('No refresh token');
  }

  const userStr = localStorage.getItem('cognitoUser');
  const user = userStr ? JSON.parse(userStr) : null;

  // Use a bare axios call (no interceptors) to avoid triggering the refresh loop
  let res;
  try {
    // A refresh that hangs (a stalled connection) fails after REFRESH_TIMEOUT_MS rather
    // than holding every request queued behind it.
    res = await axios.post(`${API_BASE_URL}/auth/cognito/refresh`, {
      refreshToken: storedRefreshToken,
      username: user?.username,
    }, { _skipTokenCheck: true, timeout: REFRESH_TIMEOUT_MS });
  } catch (error) {
    // The server refused the refresh token (expired, revoked, user disabled): signed out.
    // A 503 or no answer at all is not the session's fault.
    if (error.response?.status === 400 || error.response?.status === 401) error.sessionEnded = true;
    throw error;
  }

  if (!res.data.success) throw sessionEndedError('Refresh returned success=false');
  // Signed out (or signed in again, in another tab) while this refresh was on its way:
  // its tokens are not written back over that. A new session's token is used instead.
  if (localStorage.getItem('cognitoRefreshToken') !== storedRefreshToken) {
    const currentToken = localStorage.getItem('sessionToken');
    if (currentToken && localStorage.getItem('cognitoRefreshToken')) return currentToken;
    throw sessionEndedError('Signed out during refresh');
  }

  const { accessToken, idToken } = res.data.tokens;

  localStorage.setItem('sessionToken', accessToken);
  localStorage.setItem('cognitoAccessToken', accessToken);
  localStorage.setItem('cognitoIdToken', idToken);
  apiClient.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  // A token that is already due the moment it arrives means this computer's clock is off
  // (ahead of the server's): no background refresh for it, so it can't refresh in a loop;
  // requests refresh it when they need to, as before.
  const freshSecondsLeft = secondsUntilExpiry(accessToken);
  if (freshSecondsLeft !== null && freshSecondsLeft <= BACKGROUND_REFRESH_LEAD_SECONDS) {
    backgroundRefreshSkippedFor = accessToken;
  }
  scheduleBackgroundRefresh();

  return accessToken;
};

// ─── Background refresh ───────────────────────────────────────────────────────
// The access token is refreshed shortly before it expires (and when the tab comes back
// after being idle), so the first request after a quiet spell doesn't wait for the
// refresh call. A failed background refresh changes nothing by itself: the next request
// refreshes (and signs out on failure) exactly as before. Only requests that were
// already waiting on it get the usual failure handling.

export const BACKGROUND_REFRESH_LEAD_SECONDS = 120;
const BACKGROUND_REFRESH_RETRY_MS = 30000;
// Each tab waits a random extra moment, so with several tabs open the first one
// refreshes and the others find the new token (shared through localStorage) not due.
const BACKGROUND_REFRESH_SPREAD_MS = 15000;
let backgroundRefreshTimer = null;
let backgroundRefreshToken = null;
// The token whose background refresh last failed, so a retry waits instead of looping.
let backgroundRefreshFailedFor = null;
// A token that was already due when it arrived (clock skew): not refreshed in the background.
let backgroundRefreshSkippedFor = null;

const secondsUntilExpiry = (token) => {
  const payload = token ? decodeJWTPayload(token) : null;
  if (!payload || typeof payload.exp !== 'number') return null;
  return payload.exp - Date.now() / 1000;
};

/**
 * Refreshes the access token if it expires within BACKGROUND_REFRESH_LEAD_SECONDS.
 * Resolves 'refreshed', 'not-due' (no session, or the token has time left), 'blocked'
 * (another refresh is running, or there is no refresh token) or 'failed'.
 */
export const refreshSessionInBackground = async () => {
  const token = localStorage.getItem('sessionToken');
  const secondsLeft = secondsUntilExpiry(token);
  if (secondsLeft === null || secondsLeft > BACKGROUND_REFRESH_LEAD_SECONDS) return 'not-due';
  if (isRefreshing || !localStorage.getItem('cognitoRefreshToken')) return 'blocked';

  isRefreshing = true;
  try {
    const freshToken = await doRefresh();
    processQueue(null, freshToken);
    return 'refreshed';
  } catch (err) {
    // Requests that queued behind this refresh fail the way they would have on their own.
    if (refreshQueue.length > 0) {
      processQueue(err, null);
      endSessionIfOver(err);
    }
    return 'failed';
  } finally {
    isRefreshing = false;
  }
};

// One background attempt, then re-arm. An attempt that leaves the same token in place
// (it failed, or couldn't run) makes the next one wait BACKGROUND_REFRESH_RETRY_MS.
// (A token that arrives already due is not refreshed in the background: see doRefresh.)
const attemptBackgroundRefresh = () => {
  const token = localStorage.getItem('sessionToken');
  return refreshSessionInBackground()
    .then((result) => {
      if ((result === 'failed' || result === 'blocked') && token && localStorage.getItem('sessionToken') === token) {
        backgroundRefreshFailedFor = token;
      }
    })
    .catch(() => {})
    .finally(scheduleBackgroundRefresh);
};

/** (Re)arms the timer for the current session token; a no-op when it's already armed. */
export function scheduleBackgroundRefresh() {
  const token = localStorage.getItem('sessionToken');
  if (token === backgroundRefreshToken && (backgroundRefreshTimer || !token)) return;

  if (backgroundRefreshTimer) clearTimeout(backgroundRefreshTimer);
  backgroundRefreshTimer = null;
  backgroundRefreshToken = token;

  const secondsLeft = secondsUntilExpiry(token);
  if (secondsLeft === null || token === backgroundRefreshSkippedFor) return;
  // A second past the lead time, so the attempt finds the token due.
  let delayMs = Math.max(0, (secondsLeft - BACKGROUND_REFRESH_LEAD_SECONDS + 1) * 1000)
    + Math.floor(Math.random() * BACKGROUND_REFRESH_SPREAD_MS);
  if (token === backgroundRefreshFailedFor) {
    // Retry a failed refresh later while the token still works; once it has expired,
    // the next request refreshes it.
    if (secondsLeft <= 0) return;
    delayMs = Math.max(delayMs, BACKGROUND_REFRESH_RETRY_MS);
  }
  backgroundRefreshTimer = setTimeout(() => {
    backgroundRefreshTimer = null;
    backgroundRefreshToken = null;
    attemptBackgroundRefresh();
  }, delayMs);
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  // Timers are throttled in hidden tabs: catch up when the page is used again.
  const onReturn = () => {
    if (document.visibilityState !== 'visible') return;
    const current = localStorage.getItem('sessionToken');
    if (current === backgroundRefreshFailedFor || current === backgroundRefreshSkippedFor) return;
    attemptBackgroundRefresh();
  };
  document.addEventListener('visibilitychange', onReturn);
  window.addEventListener('focus', onReturn);
}

// ─── Shared interceptor logic ─────────────────────────────────────────────────

// Told about a request refused before it was sent (its login couldn't be refreshed), so
// something counting requests on their way (utils/apiMutationListener.js) stops counting it.
const notSentListeners = new Set();
export const onRequestNotSent = (listener) => {
  notSentListeners.add(listener);
  return () => notSentListeners.delete(listener);
};
const requestNotSent = (config) => notSentListeners.forEach((listener) => {
  try {
    listener(config);
  } catch {
    // a listener must never break the request
  }
});

const buildRequestInterceptor = (instanceName) => async (config) => {
  // Every request names the page it came from, including ones sent without a login
  config.headers = applyCurrentFrontendRouteHeader(config.headers || {});

  // Never intercept the refresh call itself
  if (config._skipTokenCheck || isRefreshCall(config.url)) return config;

  const token = localStorage.getItem('sessionToken');

  if (token && isTokenExpired(token)) {
    if (isRefreshing) {
      let freshToken;
      try {
        freshToken = await new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        });
      } catch (err) {
        if (onPublicPage()) return withoutLogin(config);
        requestNotSent(config);
        throw err;
      }
      config.headers.Authorization = `Bearer ${freshToken}`;
      return config;
    }

    isRefreshing = true;
    try {
      const freshToken = await doRefresh();
      processQueue(null, freshToken);
      config.headers.Authorization = `Bearer ${freshToken}`;
    } catch (err) {
      processQueue(err, null);
      endSessionIfOver(err);
      if (onPublicPage()) return withoutLogin(config);
      requestNotSent(config);
      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  } else {
    if (token) config.headers.Authorization = `Bearer ${token}`;
    // Arms the background refresh for a token set elsewhere (sign-in, another tab).
    if (token) scheduleBackgroundRefresh();
  }

  return config;
};

const buildResponseInterceptor = (instanceName, retryWith) => async (error) => {
  const originalRequest = error.config;

  if (
    error.response?.status === 401 &&
    !originalRequest._retry &&
    !originalRequest._skipTokenCheck &&
    !isRefreshCall(originalRequest.url)
  ) {
    // On a patient page, a staff login that can't be refreshed is dropped and the
    // request sent again without it (it needs none).
    const sentWithLogin = !!originalRequest.headers?.Authorization;
    const retryWithoutLoginOnPublicPage = (failure) => (
      onPublicPage() && sentWithLogin ? retryWith(withoutLogin(originalRequest)) : Promise.reject(failure)
    );

    const storedRefreshToken = localStorage.getItem('cognitoRefreshToken');
    if (!storedRefreshToken) {
      endSession();
      return retryWithoutLoginOnPublicPage(error);
    }

    // Retried once only, whichever refresh supplies the new token.
    originalRequest._retry = true;

    if (isRefreshing) {
      let token;
      try {
        token = await new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        });
      } catch (err) {
        return retryWithoutLoginOnPublicPage(err);
      }
      originalRequest.headers.Authorization = `Bearer ${token}`;
      return retryWith(originalRequest);
    }

    isRefreshing = true;
    let freshToken;
    try {
      freshToken = await doRefresh();
      processQueue(null, freshToken);
    } catch (refreshError) {
      processQueue(refreshError, null);
      endSessionIfOver(refreshError);
      return retryWithoutLoginOnPublicPage(refreshError);
    } finally {
      isRefreshing = false;
    }
    originalRequest.headers.Authorization = `Bearer ${freshToken}`;
    return retryWith(originalRequest);
  }

  return Promise.reject(error);
};

/**
 * Gives an axios instance the app's login handling: the token on every request, a
 * refresh before it expires, one refresh-and-retry on a 401, and the login page once
 * the session is over.
 */
export function attachAuthInterceptors(instance, instanceName) {
  instance.interceptors.request.use(
    async (config) => markAxiosRequestStarted(await buildRequestInterceptor(instanceName)(config)),
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => {
      captureAxiosResponse(response);
      return response;
    },
    async (error) => {
      // A failed refresh reaches here twice (as the refresh call's error, then as the
      // error of the request that needed it): it is reported once.
      if (!error?.capturedByPosthog) {
        captureAxiosError(error);
        if (error && typeof error === 'object') error.capturedByPosthog = true;
      }
      return buildResponseInterceptor(instanceName, (req) => instance(req))(error);
    }
  );
}

// ─── Apply interceptors to apiClient (used by store/api/* files) ──────────────

attachAuthInterceptors(apiClient, 'apiClient');

// ─── Apply interceptors to global axios (used by the rest of the app) ─────────
// 223 files use plain axios.get/post/etc. directly — this covers all of them.

attachAuthInterceptors(axios, 'axios');

// Helper to get auth headers (for non-axios requests)
export const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
  ...getFrontendRouteHeader(),
});
